import React, { useState, useEffect } from 'react'
import { DateTime } from 'luxon'
import { BUTTON_SIZE, BUTTON_TYPE } from '~/app/components'
import styled from '~/app/components/styled'
import Button from '~/app/components/common/Button/Button'
import BottomSheet from '~/app/components/common/BottomSheet/BottomSheet'
import { RentTimeLine } from '~/__generated__/__types__'
import NoticeBanner from '~/app/components/common/BottomSheet/NoticeBanner'
import { RentTimeLines } from '~/app/components/Cart/RentTimeHook'
import { Typography } from '@yanolja-rnd/ya-com-core'

/**
 * DomesticAccommodationRentTimeLine component
 * author: jaegeunlee
 * description
 */
const TimeLineContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  height: 100%;
`
const TimeLineItem = styled.div`
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 5.6rem;
  ${Typography('subtitle3').regular};
  &:hover {
    @media (hover: hover) and (pointer: fine) {
      cursor: pointer;
      background-color: ${({ theme }) => theme.color.gray100};
    }
  }
  &:active {
    background-color: ${({ theme }) => theme.color.gray200};
  }
  @media (min-width: ${({ theme }) => theme.unit.PC_WIDTH}px) {
    width: 50%;
  }
`
const TimeText = styled.span<{ needSpace?: boolean }>`
  display: inline-block;
  width: 5rem;
  margin-left: ${({ needSpace }) => (needSpace ? 1 : 0)}rem;
`
const Check = styled.img`
  width: 2.4rem;
  height: 2.4rem;
`
const ActiveTime = styled.div<{ isActive?: boolean }>`
  ${({ isActive, theme }) =>
    isActive && `color: ${theme.color.blue600}; ${Typography('subtitle3').bold};`}
`

interface DomesticAccommodationRentTimeLineProps {
  isOpened: boolean
  setOpened: (open: boolean) => void
  noticeText: string
  rentTime?: RentTimeLine
  onTimeSelect?: (rentTime: RentTimeLine) => void
  rentTimeLine: RentTimeLines
}

const TimeLineText = ({ timeLine, isActive }: { timeLine: RentTimeLine; isActive: boolean }) => {
  return (
    <ActiveTime isActive={isActive}>
      <TimeText>{timeLine.time}</TimeText> ~
      <TimeText needSpace>
        {DateTime.fromFormat(timeLine.time, 'HH:mm')
          .plus({ minutes: timeLine.useTime })
          .toFormat('HH:mm')}
      </TimeText>
    </ActiveTime>
  )
}

const DomesticAccommodationRentTimeLine: React.FC<DomesticAccommodationRentTimeLineProps> = ({
  isOpened,
  setOpened,
  noticeText,
  rentTime,
  onTimeSelect,
  rentTimeLine,
}) => {
  const [current, setCurrent] = useState<RentTimeLine | undefined>(rentTime)

  useEffect(() => {
    setCurrent(rentTime)
  }, [rentTime])

  const onTimeLineClick = (timeLine: RentTimeLine) => setCurrent(timeLine)

  const onTimeSelectClick = () => {
    if (!current || current?.time === rentTime?.time) {
      setOpened(false)
    } else {
      onTimeSelect?.(current)
    }
  }

  const isActive = (timeLine: RentTimeLine) => {
    return current?.time === timeLine.time
  }
  return (
    <BottomSheet
      isOpened={isOpened}
      onRequestClose={() => setOpened(false)}
      title={'대실 시간 변경'}
      onBackgroundClick={() => setOpened(false)}
      buttons={
        <Button
          type={BUTTON_TYPE.PRIMARY}
          size={BUTTON_SIZE.XL}
          title={'적용 하기'}
          isFullSize
          isDisabled={!current || !rentTimeLine.length}
          onClick={onTimeSelectClick}
        />
      }
      headerAppend={noticeText ? <NoticeBanner noticeBannerText={noticeText} /> : undefined}
    >
      <TimeLineContainer>
        {rentTimeLine.map(timeLine => {
          const active = isActive(timeLine)
          return (
            <TimeLineItem key={timeLine.time} onClick={() => onTimeLineClick(timeLine)}>
              <TimeLineText timeLine={timeLine} isActive={active} />
              {active && <Check src={require('../../../imgs/icon-done-line-2.svg?inline')} />}
            </TimeLineItem>
          )
        })}
      </TimeLineContainer>
    </BottomSheet>
  )
}

export default DomesticAccommodationRentTimeLine
